import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Chip, IconButton, Tooltip, CircularProgress,
  Button,
} from '@mui/material';
import {
  BeachAccess, Settings, Groups, Assignment, ListAlt, CalendarMonth, Home, Refresh,
  Article, Assessment, People, ChevronLeft, ChevronRight
} from '@mui/icons-material';
import axios from 'axios';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const LEAVE_PALETTE = ['#1565c0','#c62828','#2e7d32','#ad1457','#e65100','#4527a0','#00838f','#558b2f'];

const leaveColor = (code) =>
  LEAVE_PALETTE[Math.abs((code || '').split('').reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 0)) % LEAVE_PALETTE.length];

const initials = (name) => (name || '').split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase();

function buildCalendarDays(year, month) {
  const firstDay    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);
  while (days.length % 7 !== 0) days.push(null);
  return days;
}

function leavesOnDay(leaves, year, month, day) {
  const target = new Date(year, month, day);
  target.setHours(0, 0, 0, 0);
  return leaves.filter(l => {
    const start = new Date(l.startDate); start.setHours(0, 0, 0, 0);
    const end   = new Date(l.endDate);   end.setHours(23, 59, 59, 999);
    return target >= start && target <= end;
  });
}

const LeaveDashboard = () => {
  const navigate  = useNavigate();
  const [stats,   setStats]   = useState({});
  const [loading, setLoading] = useState(true);

  const _now = new Date();
  const [calYear,    setCalYear]    = useState(_now.getFullYear());
  const [calMonth,   setCalMonth]   = useState(_now.getMonth());
  const [calLeaves,  setCalLeaves]  = useState([]);
  const [calLoading, setCalLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [apps, onLeave] = await Promise.all([
        axios.get(`${API}/api/leave/applications`, { headers: hdrs() }),
        axios.get(`${API}/api/employees?status=On+Leave`, { headers: hdrs() }).catch(() => ({ data: [] })),
      ]);
      const pending  = apps.data.filter(a => ['Open', 'Pending Approval'].includes(a.status)).length;
      const approved = apps.data.filter(a => a.status === 'Approved').length;
      setStats({ total: apps.data.length, pending, approved, onLeave: onLeave.data.length });
    } catch {
      setStats({ total: 0, pending: 0, approved: 0, onLeave: 0 });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    setCalLoading(true);
    axios.get(`${API}/api/leave/applications?status=Approved`, { headers: hdrs() })
      .then(r => setCalLeaves(r.data || []))
      .catch(() => setCalLeaves([]))
      .finally(() => setCalLoading(false));
  }, []); // eslint-disable-line

  const navMonth = (dir) => {
    let y = calYear, m = calMonth + dir;
    if (m < 0)  { m = 11; y -= 1; }
    if (m > 11) { m = 0;  y += 1; }
    setCalYear(y); setCalMonth(m);
  };

  const kpis = [
    { label: 'Pending Applications', value: stats.pending ?? 0,  color: '#e65100', bg: '#fff3e0', path: '/hr/leave/applications?status=Open' },
    { label: 'Approved This Year',   value: stats.approved ?? 0, color: '#1565c0', bg: '#e3f2fd', path: '/hr/leave/applications?status=Approved' },
    { label: 'Currently On Leave',   value: stats.onLeave ?? 0,  color: '#2e7d32', bg: '#e8f5e9', path: '/hr/employees?status=On+Leave' },
    { label: 'Total Applications',   value: stats.total ?? 0,    color: '#546e7a', bg: '#eceff1', path: '/hr/leave/applications' },
  ];

  const modules = [
    { icon: People,        label: 'Employees',          desc: 'Leave groups, balances and quick links',  path: '/hr/leave/employees',    color: '#00695c' },
    { icon: Assignment,    label: 'Leave Applications', desc: 'Submit and manage leave requests',        path: '/hr/leave/applications', color: '#1565c0' },
    { icon: ListAlt,       label: 'Leave Ledger',       desc: 'View balances, allocate and carry forward', path: '/hr/leave/ledger',       color: '#2e7d32' },
    { icon: Settings,      label: 'Leave Types',        desc: 'Configure leave types and entitlements',  path: '/hr/leave/types',        color: '#37474f' },
    { icon: Groups,        label: 'Leave Groups',       desc: 'Group-based entitlement overrides',       path: '/hr/leave/groups',       color: '#6a1b9a' },
    { icon: Article,       label: 'Leave Statement',    desc: 'Per-employee leave statement with history', path: '/hr/leave/statement',    color: '#00838f' },
    { icon: Assessment,    label: 'Leave Report',       desc: 'All-employee leave summary and balances',  path: '/hr/leave/report',       color: '#1b5e20' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8', py: 3 }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto', px: { xs: 2, sm: 3 } }}>

        {/* Header */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 3, p: { xs: 2, sm: 3 }, borderRadius: 3,
          background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
          color: '#fff', boxShadow: 3, flexWrap: 'wrap', gap: 2,
        }}>
          <Box>
            <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
              <BeachAccess sx={{ fontSize: 32 }} />
              <Typography variant="h5" fontWeight={700}>Leave Management</Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Leave Applications · Balances · Allocation · Carry Forward
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={load} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <Refresh />}
              </IconButton>
            </Tooltip>
            <Tooltip title="HR Home">
              <IconButton onClick={() => navigate('/hr')} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                <Home />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* KPI tiles */}
        <Typography variant="subtitle1" fontWeight={700} mb={1.5}>Activities</Typography>
        <Grid container spacing={2} mb={3}>
          {kpis.map(({ label, value, color, bg, path }) => (
            <Grid item xs={6} md={3} key={label}>
              <Card elevation={2} onClick={() => navigate(path)} sx={{
                borderRadius: 3, borderLeft: `4px solid ${color}`,
                cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s',
                '&:hover': { boxShadow: 5, transform: 'translateY(-3px)' },
              }}>
                <CardContent sx={{ py: 1.8 }}>
                  <Typography variant="h4" fontWeight={800} color={color} lineHeight={1}>
                    {loading ? '…' : value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Module tiles */}
        <Typography variant="subtitle1" fontWeight={700} mb={1.5}>Modules</Typography>
        <Grid container spacing={2}>
          {modules.map(({ icon: Icon, label, desc, path, color }) => (
            <Grid item xs={12} sm={6} key={label}>
              <Card sx={{
                borderRadius: 3, cursor: 'pointer', borderTop: `4px solid ${color}`,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' },
              }} onClick={() => navigate(path)}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box sx={{
                      width: 46, height: 46, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: `${color}1A`,
                    }}>
                      <Icon sx={{ fontSize: 24, color }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
                      <Typography variant="body2" color="text.secondary" fontSize={12} mt={0.3}>{desc}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Leave Calendar */}
        <Box mt={3}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent>
              {/* Calendar header */}
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CalendarMonth sx={{ color: '#1565c0', fontSize: 22 }} />
                  <Typography variant="subtitle1" fontWeight={700}>Leave Calendar</Typography>
                  {calLoading && <CircularProgress size={14} sx={{ ml: 0.5 }} />}
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <IconButton size="small" onClick={() => navMonth(-1)}><ChevronLeft /></IconButton>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ minWidth: 140, textAlign: 'center' }}>
                    {MONTH_NAMES[calMonth]} {calYear}
                  </Typography>
                  <IconButton size="small" onClick={() => navMonth(1)}><ChevronRight /></IconButton>
                  <Button size="small" variant="outlined" onClick={() => { const n = new Date(); setCalYear(n.getFullYear()); setCalMonth(n.getMonth()); }}
                    sx={{ ml: 1, py: 0.3, px: 1.2, fontSize: 11, minWidth: 0 }}>Today</Button>
                </Box>
              </Box>

              {/* Day-of-week row */}
              <Box display="grid" sx={{ gridTemplateColumns: 'repeat(7, 1fr)' }} mb={0.5}>
                {DAY_NAMES.map(d => (
                  <Typography key={d} variant="caption" fontWeight={700} color="text.secondary"
                    sx={{ textAlign: 'center', py: 0.5, bgcolor: '#f5f5f5', borderRadius: '4px 4px 0 0' }}>
                    {d}
                  </Typography>
                ))}
              </Box>

              {/* Calendar grid */}
              <Box display="grid" sx={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', bgcolor: '#e0e0e0',
                border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                {buildCalendarDays(calYear, calMonth).map((day, idx) => {
                  const dayLeaves = day ? leavesOnDay(calLeaves, calYear, calMonth, day) : [];
                  const n = new Date();
                  const isToday = day && n.getFullYear() === calYear && n.getMonth() === calMonth && n.getDate() === day;
                  return (
                    <Box key={idx} sx={{ bgcolor: day ? '#fff' : '#fafafa', minHeight: 78, p: '4px 5px' }}>
                      {day && (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.3 }}>
                            <Typography variant="caption" sx={{
                              width: 22, height: 22, lineHeight: '22px', textAlign: 'center', borderRadius: '50%',
                              fontWeight: isToday ? 700 : 400,
                              bgcolor: isToday ? '#1565c0' : 'transparent',
                              color: isToday ? '#fff' : 'text.secondary',
                            }}>{day}</Typography>
                          </Box>
                          {dayLeaves.slice(0, 2).map((l, li) => (
                            <Tooltip key={li} title={`${l.employeeName} — ${l.leaveTypeName || l.leaveTypeCode}`} arrow>
                              <Chip label={initials(l.employeeName)} size="small" sx={{
                                height: 17, fontSize: 10, mb: 0.2, width: '100%', justifyContent: 'flex-start', cursor: 'default',
                                bgcolor: leaveColor(l.leaveTypeCode) + '20',
                                color: leaveColor(l.leaveTypeCode),
                                border: `1px solid ${leaveColor(l.leaveTypeCode)}50`,
                                '& .MuiChip-label': { px: 0.8 },
                              }} />
                            </Tooltip>
                          ))}
                          {dayLeaves.length > 2 && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block', textAlign: 'right' }}>
                              +{dayLeaves.length - 2}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {/* Legend */}
              {calLeaves.length > 0 && (
                <Box mt={1.5} display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Leave types:</Typography>
                  {[...new Map(calLeaves.map(l => [l.leaveTypeCode, l.leaveTypeName || l.leaveTypeCode])).entries()].map(([code, name]) => (
                    <Box key={code} display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: leaveColor(code), flexShrink: 0 }} />
                      <Typography variant="caption" color="text.secondary">{name}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

      </Box>
    </Box>
  );
};

export default LeaveDashboard;
